import { Link } from "react-router-dom";
import { ButtonLink } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { CameraIcon, ChevronRightIcon, SparkleIcon, WrenchIcon } from "../ui/icons.js";

/**
 * Authenticated landing screen.
 *
 * Goal (design.md §9): make the first action obvious and reassuring.
 * Pattern: friendly headline + single primary CTA + a quiet "how it works"
 * recap so the user understands the loop before submitting their first RFQ.
 */
export function AppHome() {
  return (
    <section className="mx-auto max-w-md px-5 pt-8">
      <header className="mb-8">
        <h1 className="text-[28px] font-bold leading-tight tracking-tight text-slate-900">
          Welcome to QC Home and Business Project Pro's
        </h1>
        <p className="mt-2 text-base leading-relaxed text-slate-600">
          Describe what needs doing — we'll help you find the right person for the job.
        </p>
      </header>

      <ButtonLink to="/app/rfqs/new" size="lg" fullWidth>
        Start a new request
      </ButtonLink>

      <div className="mt-10">
        <h2 className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
          How it works
        </h2>
        <Card className="mt-3 divide-y divide-slate-100">
          <Step
            icon={<CameraIcon className="h-5 w-5" />}
            title="Snap a few photos"
            body="Show what you need done — no jargon required."
          />
          <Step
            icon={<WrenchIcon className="h-5 w-5" />}
            title="Tell us the basics"
            body="Service type, urgency, and the property it's at."
          />
          <Step
            icon={<SparkleIcon className="h-5 w-5" />}
            title="Get matched"
            body="We connect you with vetted local pros who can quote."
          />
        </Card>

        <Link
          to="/app/rfqs"
          className="press mt-4 flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <span>View your requests</span>
          <ChevronRightIcon className="h-4 w-4 text-slate-400" />
        </Link>
      </div>
    </section>
  );
}

function Step({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex items-start gap-3 p-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="mt-0.5 text-sm leading-relaxed text-slate-600">{body}</p>
      </div>
    </div>
  );
}
